<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const iframeLoading = ref(true);
const iframeRef = ref(null);
const iframeSrc = ref('');

const handleMessage = event => {
  // debug logs removed
  // Strings may be JSON-encoded messages or legacy updates
  if (typeof event.data === 'string') {
    // Try JSON parse first
    try {
      const parsed = JSON.parse(event.data);
      if (parsed && typeof parsed === 'object') {
        const { type, content } = parsed;
        if (type === 'csml-content' || type === 'csml-save') {
          if (
            content === undefined ||
            content === null ||
            content === 'undefined'
          ) {
            return;
          }
          emit('update:modelValue', content);
          return;
        }
        // csml-ready will be handled after function declarations
      }
    } catch (e) {
      // Not JSON; fall back to legacy string protocol
    }

    if (event.data.startsWith('chatwoot-csml-editor:update')) {
      const csmlContent = event.data.replace('chatwoot-csml-editor:update', '');
      // debug logs removed
      if (csmlContent === 'undefined') return;
      emit('update:modelValue', csmlContent);
    }
    return;
  }

  // Support object-based protocol from newer editor builds
  if (typeof event.data === 'object' && event.data) {
    const { type, content } = event.data;
    if (type === 'csml-content' || type === 'csml-save') {
      if (
        content === undefined ||
        content === null ||
        content === 'undefined'
      ) {
        return;
      }
      // debug logs removed
      emit('update:modelValue', content);
    }
    // csml-ready will be handled after function declarations
  }
};

const sendContentToEditor = () => {
  const frameElement = iframeRef.value;
  if (frameElement && !iframeLoading.value) {
    const eventData = {
      event: 'editorContext',
      content: props.modelValue || '',
      data: props.modelValue || '',
    };
    // debug logs removed
    frameElement.contentWindow.postMessage(JSON.stringify(eventData), '*');
  }
};

const onEditorLoad = () => {
  const frameElement = iframeRef.value;
  if (frameElement) {
    const eventData = {
      event: 'editorContext',
      content: props.modelValue || '',
      data: props.modelValue || '',
    };
    // debug logs removed
    frameElement.contentWindow.postMessage(JSON.stringify(eventData), '*');
  }
  iframeLoading.value = false;
};

// Watch for changes in modelValue and send to editor
watch(() => props.modelValue, sendContentToEditor);

onMounted(() => {
  // Build initial iframe src with content so the external editor preloads it
  const host =
    window.globalConfig?.csmlEditorHost ||
    window.globalConfig?.CSML_EDITOR_HOST ||
    'https://csml-editor.cdn.etisalna.com';
  const initial = encodeURIComponent(props.modelValue || '');
  // ts param to avoid cache retaining older content
  // Provide both keys (content and data) to support different editor builds
  iframeSrc.value = `${host}/?content=${initial}&data=${initial}&ts=${Date.now()}`;
  // debug logs removed

  window.addEventListener('message', handleMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', handleMessage);
});

// 'csml-ready' handler
window.addEventListener('message', evt => {
  try {
    const data = typeof evt.data === 'string' ? JSON.parse(evt.data) : evt.data;
    if (data && data.type === 'csml-ready') {
      onEditorLoad();
    }
  } catch (_) {
    // ignore
  }
});
</script>

<template>
  <div class="csml-editor--container">
    <div
      v-if="iframeLoading"
      class="flex items-center justify-center h-full bg-gray-100"
    >
      <div class="text-center">
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"
        />
        <p class="text-sm text-gray-600">
          {{ $t('AGENT_BOTS.LOADING_EDITOR') }}
        </p>
      </div>
    </div>
    <iframe
      id="csml-editor--frame"
      ref="iframeRef"
      class="csml-editor-frame"
      :src="iframeSrc"
      @load="onEditorLoad"
    />
  </div>
</template>

<style scoped>
.csml-editor--container {
  position: relative;
  width: 100%;
  height: 100%;
}

.csml-editor-frame {
  width: 100%;
  height: 100%;
  border: none;
}
</style>
